import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Search, Plus, Edit, Trash2, Truck, User, MapPin, Fuel, Wrench, Building2, CheckCircle, XCircle, Phone, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FleetPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('trucks');
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Data states
  const [trucks, setTrucks] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [truckStops, setTruckStops] = useState([]);
  const [mechanics, setMechanics] = useState([]);
  const [offices, setOffices] = useState([]);
  
  // Dialog states
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({});

  const isAdmin = user?.role_id === 'master_admin' || user?.role_id === 'admin';

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchData();
  }, [activeTab, search]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/fleet/stats`, { withCredentials: true });
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      let res;
      switch (activeTab) {
        case 'trucks':
          res = await axios.get(`${API}/fleet/trucks${params}`, { withCredentials: true });
          setTrucks(res.data.trucks || []);
          break;
        case 'drivers':
          res = await axios.get(`${API}/fleet/drivers${params}`, { withCredentials: true });
          setDrivers(res.data.drivers || []);
          break;
        case 'locations':
          res = await axios.get(`${API}/fleet/locations${params}`, { withCredentials: true });
          setLocations(res.data.locations || []);
          break;
        case 'truckStops':
          res = await axios.get(`${API}/fleet/truck-stops${params}`, { withCredentials: true });
          setTruckStops(res.data.truck_stops || []);
          break;
        case 'mechanics':
          res = await axios.get(`${API}/fleet/mechanics${params}`, { withCredentials: true });
          setMechanics(res.data.mechanics || []);
          break;
        case 'offices':
          res = await axios.get(`${API}/fleet/offices${params}`, { withCredentials: true });
          setOffices(res.data.offices || []);
          break;
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const openCreate = () => {
    setEditingItem(null);
    setFormData(getDefaultForm());
    setShowDialog(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({ ...item });
    setShowDialog(true);
  };

  const getDefaultForm = () => {
    switch (activeTab) {
      case 'trucks': return { truck_number: '', plate_number: '', vin: '', make: '', model: '', year: '', status: 'active' };
      case 'drivers': return { name: '', email: '', phone: '', license_number: '', license_expiry: '', status: 'active' };
      case 'locations': return { name: '', address: '', location_type: 'other', latitude: '', longitude: '' };
      case 'truckStops': return { name: '', address: '', phone: '', has_fuel: true, has_parking: true, has_showers: false, has_restaurant: false };
      case 'mechanics': return { name: '', phone: '', email: '', specialty: '', shop_name: '', address: '', status: 'active' };
      case 'offices': return { name: '', address: '', phone: '', office_type: 'branch' };
      default: return {};
    }
  };

  const getEndpoint = () => {
    switch (activeTab) {
      case 'trucks': return 'trucks';
      case 'drivers': return 'drivers';
      case 'locations': return 'locations';
      case 'truckStops': return 'truck-stops';
      case 'mechanics': return 'mechanics';
      case 'offices': return 'offices';
      default: return '';
    }
  };

  const getIdField = () => {
    switch (activeTab) {
      case 'trucks': return 'truck_id';
      case 'drivers': return 'driver_id';
      case 'locations': return 'location_id';
      case 'truckStops': return 'stop_id';
      case 'mechanics': return 'mechanic_id';
      case 'offices': return 'office_id';
      default: return 'id';
    }
  };

  const handleSave = async () => {
    try {
      const endpoint = getEndpoint();
      const payload = { ...formData };
      if (payload.year) payload.year = parseInt(payload.year);
      // Handle latitude/longitude - convert to float or remove if empty
      if (payload.latitude !== undefined && payload.latitude !== '') {
        payload.latitude = parseFloat(payload.latitude);
      } else {
        delete payload.latitude;
      }
      if (payload.longitude !== undefined && payload.longitude !== '') {
        payload.longitude = parseFloat(payload.longitude);
      } else {
        delete payload.longitude;
      }

      if (editingItem) {
        await axios.patch(`${API}/fleet/${endpoint}/${editingItem[getIdField()]}`, payload, { withCredentials: true });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/fleet/${endpoint}`, payload, { withCredentials: true });
        toast.success('Created successfully');
      }
      setShowDialog(false);
      fetchData();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    }
  };

  const handleDelete = async () => {
    try {
      const endpoint = getEndpoint();
      await axios.delete(`${API}/fleet/${endpoint}/${deleteConfirm[getIdField()]}`, { withCredentials: true });
      toast.success('Deleted successfully');
      setDeleteConfirm(null);
      fetchData();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const getStatusBadge = (status) => {
    if (status === 'active') return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
    if (status === 'maintenance') return <Badge className="bg-yellow-500/20 text-yellow-400"><Wrench className="w-3 h-3 mr-1" />Maintenance</Badge>;
    return <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" />Inactive</Badge>;
  };

  const renderForm = () => {
    switch (activeTab) {
      case 'trucks':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Truck Number *</Label><Input value={formData.truck_number || ''} onChange={(e) => setFormData({...formData, truck_number: e.target.value})} placeholder="T-101" /></div>
              <div><Label>Plate Number</Label><Input value={formData.plate_number || ''} onChange={(e) => setFormData({...formData, plate_number: e.target.value})} placeholder="ABC-1234" /></div>
            </div>
            <div><Label>VIN</Label><Input value={formData.vin || ''} onChange={(e) => setFormData({...formData, vin: e.target.value})} placeholder="Vehicle ID Number" /></div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Make</Label><Input value={formData.make || ''} onChange={(e) => setFormData({...formData, make: e.target.value})} placeholder="Freightliner" /></div>
              <div><Label>Model</Label><Input value={formData.model || ''} onChange={(e) => setFormData({...formData, model: e.target.value})} placeholder="Cascadia" /></div>
              <div><Label>Year</Label><Input type="number" value={formData.year || ''} onChange={(e) => setFormData({...formData, year: e.target.value})} placeholder="2023" /></div>
            </div>
            <div><Label>Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem><SelectItem value="maintenance">Maintenance</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
        );
      case 'drivers':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="John Smith" /></div>
              <div><Label>Email *</Label><Input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="john@company.com" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" /></div>
              <div><Label>License Number</Label><Input value={formData.license_number || ''} onChange={(e) => setFormData({...formData, license_number: e.target.value})} placeholder="DL12345678" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>License Expiry</Label><Input type="date" value={formData.license_expiry || ''} onChange={(e) => setFormData({...formData, license_expiry: e.target.value})} /></div>
              <div><Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      case 'locations':
        return (
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Main Warehouse" /></div>
            <div><Label>Address</Label><Input value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="123 Main St, City, State" /></div>
            <div><Label>Type</Label>
              <Select value={formData.location_type} onValueChange={(v) => setFormData({...formData, location_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="warehouse">Warehouse</SelectItem><SelectItem value="yard">Yard</SelectItem><SelectItem value="customer">Customer</SelectItem><SelectItem value="other">Other</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Latitude</Label><Input type="number" step="any" value={formData.latitude || ''} onChange={(e) => setFormData({...formData, latitude: e.target.value})} placeholder="41.8781" /></div>
              <div><Label>Longitude</Label><Input type="number" step="any" value={formData.longitude || ''} onChange={(e) => setFormData({...formData, longitude: e.target.value})} placeholder="-87.6298" /></div>
            </div>
          </div>
        );
      case 'truckStops':
        return (
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Flying J Travel Center" /></div>
            <div><Label>Address</Label><Input value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="123 Highway Rd" /></div>
            <div><Label>Phone</Label><Input value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-2"><Checkbox checked={formData.has_fuel} onCheckedChange={(c) => setFormData({...formData, has_fuel: c})} /><Label>Has Fuel</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={formData.has_parking} onCheckedChange={(c) => setFormData({...formData, has_parking: c})} /><Label>Has Parking</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={formData.has_showers} onCheckedChange={(c) => setFormData({...formData, has_showers: c})} /><Label>Has Showers</Label></div>
              <div className="flex items-center gap-2"><Checkbox checked={formData.has_restaurant} onCheckedChange={(c) => setFormData({...formData, has_restaurant: c})} /><Label>Has Restaurant</Label></div>
            </div>
          </div>
        );
      case 'mechanics':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Mike's Auto" /></div>
              <div><Label>Shop Name</Label><Input value={formData.shop_name || ''} onChange={(e) => setFormData({...formData, shop_name: e.target.value})} placeholder="Mike's Truck Repair" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" /></div>
              <div><Label>Email</Label><Input type="email" value={formData.email || ''} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="mike@shop.com" /></div>
            </div>
            <div><Label>Address</Label><Input value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="456 Industrial Blvd" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Specialty</Label>
                <Select value={formData.specialty || ''} onValueChange={(v) => setFormData({...formData, specialty: v})}>
                  <SelectTrigger><SelectValue placeholder="Select specialty" /></SelectTrigger>
                  <SelectContent><SelectItem value="general">General</SelectItem><SelectItem value="engine">Engine</SelectItem><SelectItem value="brakes">Brakes</SelectItem><SelectItem value="electrical">Electrical</SelectItem><SelectItem value="transmission">Transmission</SelectItem></SelectContent>
                </Select>
              </div>
              <div><Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      case 'offices':
        return (
          <div className="space-y-4">
            <div><Label>Name *</Label><Input value={formData.name || ''} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Main Office" /></div>
            <div><Label>Address</Label><Input value={formData.address || ''} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="100 Corporate Dr" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Phone</Label><Input value={formData.phone || ''} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="(555) 123-4567" /></div>
              <div><Label>Type</Label>
                <Select value={formData.office_type} onValueChange={(v) => setFormData({...formData, office_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="hq">Headquarters</SelectItem><SelectItem value="branch">Branch</SelectItem><SelectItem value="dispatch">Dispatch</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );
      default: return null;
    }
  };

  const renderTable = () => {
    const data = { trucks, drivers, locations, truckStops, mechanics, offices }[activeTab] || [];
    if (isLoading) return <div className="p-8 text-center text-muted-foreground">Loading...</div>;
    if (data.length === 0) return <div className="p-8 text-center text-muted-foreground">No data found</div>;

    switch (activeTab) {
      case 'trucks':
        return (
          <Table>
            <TableHeader><TableRow><TableHead>Truck #</TableHead><TableHead>Plate</TableHead><TableHead>Make/Model</TableHead><TableHead>Year</TableHead><TableHead>Status</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {trucks.map((t) => (
                <TableRow key={t.truck_id}>
                  <TableCell className="font-bold">{t.truck_number}</TableCell>
                  <TableCell>{t.plate_number || '-'}</TableCell>
                  <TableCell>{t.make} {t.model}</TableCell>
                  <TableCell>{t.year || '-'}</TableCell>
                  <TableCell>{getStatusBadge(t.status)}</TableCell>
                  {isAdmin && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(t)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteConfirm(t)}><Trash2 className="w-4 h-4" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      case 'drivers':
        return (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead><TableHead>License</TableHead><TableHead>Status</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {drivers.map((d) => (
                <TableRow key={d.driver_id}>
                  <TableCell className="font-bold">{d.name}</TableCell>
                  <TableCell><div className="flex items-center gap-1"><Mail className="w-3 h-3" />{d.email}</div></TableCell>
                  <TableCell>{d.phone || '-'}</TableCell>
                  <TableCell>{d.license_number || '-'}</TableCell>
                  <TableCell>{getStatusBadge(d.status)}</TableCell>
                  {isAdmin && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(d)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteConfirm(d)}><Trash2 className="w-4 h-4" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      case 'locations':
        return (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Address</TableHead><TableHead>Type</TableHead><TableHead>Coordinates</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {locations.map((l) => (
                <TableRow key={l.location_id}>
                  <TableCell className="font-bold">{l.name}</TableCell>
                  <TableCell>{l.address || '-'}</TableCell>
                  <TableCell><Badge variant="outline">{l.location_type}</Badge></TableCell>
                  <TableCell>{l.latitude && l.longitude ? `${l.latitude}, ${l.longitude}` : '-'}</TableCell>
                  {isAdmin && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(l)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteConfirm(l)}><Trash2 className="w-4 h-4" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      case 'truckStops':
        return (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Address</TableHead><TableHead>Phone</TableHead><TableHead>Amenities</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {truckStops.map((s) => (
                <TableRow key={s.stop_id}>
                  <TableCell className="font-bold">{s.name}</TableCell>
                  <TableCell>{s.address || '-'}</TableCell>
                  <TableCell>{s.phone || '-'}</TableCell>
                  <TableCell><div className="flex gap-1 flex-wrap">{s.has_fuel && <Badge variant="outline" className="text-xs">Fuel</Badge>}{s.has_parking && <Badge variant="outline" className="text-xs">Parking</Badge>}{s.has_showers && <Badge variant="outline" className="text-xs">Showers</Badge>}{s.has_restaurant && <Badge variant="outline" className="text-xs">Food</Badge>}</div></TableCell>
                  {isAdmin && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(s)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteConfirm(s)}><Trash2 className="w-4 h-4" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      case 'mechanics':
        return (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Shop</TableHead><TableHead>Contact</TableHead><TableHead>Specialty</TableHead><TableHead>Status</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {mechanics.map((m) => (
                <TableRow key={m.mechanic_id}>
                  <TableCell className="font-bold">{m.name}</TableCell>
                  <TableCell>{m.shop_name || '-'}</TableCell>
                  <TableCell><div className="flex flex-col text-sm">{m.phone && <span><Phone className="w-3 h-3 inline mr-1" />{m.phone}</span>}{m.email && <span><Mail className="w-3 h-3 inline mr-1" />{m.email}</span>}</div></TableCell>
                  <TableCell>{m.specialty ? <Badge variant="outline">{m.specialty}</Badge> : '-'}</TableCell>
                  <TableCell>{getStatusBadge(m.status)}</TableCell>
                  {isAdmin && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(m)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteConfirm(m)}><Trash2 className="w-4 h-4" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      case 'offices':
        return (
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Address</TableHead><TableHead>Phone</TableHead><TableHead>Type</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
            <TableBody>
              {offices.map((o) => (
                <TableRow key={o.office_id}>
                  <TableCell className="font-bold">{o.name}</TableCell>
                  <TableCell>{o.address || '-'}</TableCell>
                  <TableCell>{o.phone || '-'}</TableCell>
                  <TableCell><Badge variant="outline">{o.office_type === 'hq' ? 'Headquarters' : o.office_type === 'dispatch' ? 'Dispatch' : 'Branch'}</Badge></TableCell>
                  {isAdmin && <TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => openEdit(o)}><Edit className="w-4 h-4" /></Button><Button variant="ghost" size="sm" className="text-red-500" onClick={() => setDeleteConfirm(o)}><Trash2 className="w-4 h-4" /></Button></TableCell>}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        );
      default: return null;
    }
  };

  const tabLabels = {
    trucks: 'Trucks',
    drivers: 'Drivers',
    locations: 'Locations',
    truckStops: 'Truck Stops',
    mechanics: 'Mechanics',
    offices: 'Offices'
  };

  return (
    <DashboardLayout>
      <Header title="Fleet Database" subtitle="Centralized data visible to all connected apps" />
      
      <div className="p-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab('trucks')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center"><Truck className="w-5 h-5 text-blue-500" /></div>
              <div><p className="text-xl font-bold">{stats.trucks?.total || 0}</p><p className="text-xs text-muted-foreground">Trucks</p></div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab('drivers')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 flex items-center justify-center"><User className="w-5 h-5 text-green-500" /></div>
              <div><p className="text-xl font-bold">{stats.drivers?.total || 0}</p><p className="text-xs text-muted-foreground">Drivers</p></div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab('locations')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-purple-500" /></div>
              <div><p className="text-xl font-bold">{stats.locations || 0}</p><p className="text-xs text-muted-foreground">Locations</p></div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab('truckStops')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-500/10 flex items-center justify-center"><Fuel className="w-5 h-5 text-orange-500" /></div>
              <div><p className="text-xl font-bold">{stats.truck_stops || 0}</p><p className="text-xs text-muted-foreground">Truck Stops</p></div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab('mechanics')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center"><Wrench className="w-5 h-5 text-red-500" /></div>
              <div><p className="text-xl font-bold">{stats.mechanics || 0}</p><p className="text-xs text-muted-foreground">Mechanics</p></div>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-primary/50 transition-colors" onClick={() => setActiveTab('offices')}>
            <CardContent className="p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center"><Building2 className="w-5 h-5 text-cyan-500" /></div>
              <div><p className="text-xl font-bold">{stats.offices || 0}</p><p className="text-xs text-muted-foreground">Offices</p></div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs & Content */}
        <Card>
          <CardContent className="p-0">
            <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSearch(''); }}>
              <div className="flex flex-col md:flex-row md:items-center justify-between p-4 border-b gap-4">
                <TabsList className="bg-muted/50">
                  <TabsTrigger value="trucks"><Truck className="w-4 h-4 mr-1" />Trucks</TabsTrigger>
                  <TabsTrigger value="drivers"><User className="w-4 h-4 mr-1" />Drivers</TabsTrigger>
                  <TabsTrigger value="locations"><MapPin className="w-4 h-4 mr-1" />Locations</TabsTrigger>
                  <TabsTrigger value="truckStops"><Fuel className="w-4 h-4 mr-1" />Stops</TabsTrigger>
                  <TabsTrigger value="mechanics"><Wrench className="w-4 h-4 mr-1" />Mechanics</TabsTrigger>
                  <TabsTrigger value="offices"><Building2 className="w-4 h-4 mr-1" />Offices</TabsTrigger>
                </TabsList>
                <div className="flex gap-2">
                  <div className="relative flex-1 md:w-64">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
                  </div>
                  {isAdmin && <Button onClick={openCreate}><Plus className="w-4 h-4 mr-1" />Add {tabLabels[activeTab].slice(0, -1)}</Button>}
                </div>
              </div>
              <TabsContent value={activeTab} className="mt-0">{renderTable()}</TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit' : 'Add'} {tabLabels[activeTab].slice(0, -1)}</DialogTitle>
            <DialogDescription>{editingItem ? 'Update the details below' : 'Fill in the details to add a new entry'}</DialogDescription>
          </DialogHeader>
          {renderForm()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>{editingItem ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Delete</DialogTitle>
            <DialogDescription>Are you sure you want to delete this item? This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirm(null)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
